import {
  Slot as ApiSlot,
  CreateSlotBody,
  SlotRepeat,
  SlotType,
  UpdateSlotBodyBookedStrategy,
  UpdateSlotBodyScope,
  UpdateSlotBodyUpdates
} from '@api/models';

import { ApiClient } from '@website/lib/api';
import { ApiError } from '@website/lib/api/apiError';

export type SlotPaymentMethod = 'cash' | 'online';

/** UI delivery mode - the pro picks one; 'both' means the client chooses at booking. */
export type SlotDeliveryMode = 'in_person' | 'online' | 'both';

/** Location kind, as it lives on an activity_location row (never on the slot). */
export type SlotLocationKind = 'venue' | 'at_home' | 'online';

/**
 * Derive the UI 3-way mode from the slot's `locationIds`, cross-referenced with
 * the activity's location kinds (the kind lives on the activity_location, not the
 * slot). A slot is online when ANY of its ids resolves to an `online` kind, and
 * in-person when ANY id resolves to a non-online kind (venue/at_home) or its id's
 * kind is unknown (legacy/unprovisioned counts as in-person). An empty id set is
 * the in-person default. A slot that carries both is 'both'.
 *
 * Mirrors the occurrence derivation in `useAvailableSlotsQuery`.
 */
export function deliveryModeOf(
  locationIds: readonly string[] | null | undefined,
  locationKindById: Map<string, SlotLocationKind>
): SlotDeliveryMode {
  const ids = locationIds ?? [];
  const online = ids.some((id) => locationKindById.get(id) === 'online');
  const inPerson = ids.some((id) => locationKindById.get(id) !== 'online') || ids.length === 0;
  if (inPerson && online) return 'both';
  if (online) return 'online';
  return 'in_person';
}

/** A single bookable time window. Multiple windows = multiple slots. */
export interface SlotTimeWindow {
  startTime: number;
  endTime: number;
  price: number;
  /** YYYY-MM-DD anchor date for this window (one-off windows carry their own date). */
  date?: string;
}

/**
 * UI representation of a slot (a recurring rule or a one-off). A slot has one time
 * window; `timeWindows` keeps a single-element array for components that iterate
 * windows the way the old recurring model did.
 */
export interface Slot {
  id: string;
  label?: string;
  activityId: string;
  type: SlotType;
  maxPeople?: number;
  startDate: string; // anchor date
  closeDate?: string;
  repeat: SlotRepeat;
  repeatUntil?: string;
  startTime: number;
  endTime: number;
  price: number;
  /** RANGE only - the exact bookable durations in minutes. */
  fixedDurations?: number[];
  /** RANGE only - the flat price for each bookable duration (map keyed by minutes, as strings on the wire). */
  priceByDuration?: Record<string, number>;
  status: string;
  paymentMethods?: SlotPaymentMethod[];
  /** A client's first session with the pro must be paid online (cash from their second). */
  firstBookingOnline?: boolean;
  /** Delivery mode (in person / online / both). Defaults to in-person. */
  deliveryMode?: SlotDeliveryMode;
  /** The activity_location ids the slot is offered at (the picked in-person / online). */
  locationIds?: string[];
  /**
   * Derived (not from the API): the in-person activity_location id (venue/at_home)
   * resolved from `locationIds` against the activity's location kinds. Undefined
   * when the activity locations weren't supplied to `mapApiSlot` or the slot is
   * online-only.
   */
  physicalLocationId?: string;
  /**
   * Derived (not from the API): the online activity_location id resolved from
   * `locationIds` against the activity's location kinds. Undefined when none.
   */
  onlineLocationId?: string;
  /** IANA venue timezone of the parent activity - resolve the wall-clock to render in browser time. */
  timezone?: string;
}

export interface CreateSlotRequest {
  activityId: string;
  label?: string;
  type: SlotType;
  maxPeople?: number;
  /** Anchor date (YYYY-MM-DD) for recurring rules; ignored for per-window dates. */
  startDate: string;
  closeDate?: string;
  repeat: SlotRepeat;
  repeatUntil?: string;
  /** One window per slot to create. */
  timeWindows: SlotTimeWindow[];
  acceptsCash?: boolean;
  acceptsOnline?: boolean;
  firstBookingOnline?: boolean;
  /**
   * The activity_location ids the slot is offered at. Empty -> the backend
   * defaults to the activity's in-person location. The KIND lives on each
   * activity_location, not on the slot.
   */
  locationIds?: string[];
  /** RANGE only - the exact bookable durations (minutes). */
  fixedDurations?: number[];
  /** RANGE only - the flat price for each bookable duration (map keyed by minutes, as strings on the wire). */
  priceByDuration?: Record<string, number>;
}

function paymentMethodsOf(slot: ApiSlot): SlotPaymentMethod[] {
  const methods: SlotPaymentMethod[] = [];
  if (slot.acceptsCash) methods.push('cash');
  if (slot.acceptsOnline) methods.push('online');
  return methods;
}

/**
 * Map an API slot payload to the UI Slot shape.
 *
 * The API slot carries only `locationIds` (opaque ids); the KIND lives on the
 * activity_location, so pass `activityLocations` (the parent activity's
 * id->kind rows) to derive `deliveryMode` and the physical/online id split. When
 * omitted (call sites without the activity in scope), the slot falls back to the
 * in-person default with no physical/online split.
 */
export function mapApiSlot(
  s: ApiSlot,
  activityLocations?: ReadonlyArray<{ id: string; kind: SlotLocationKind }>
): Slot {
  const locationKindById = new Map<string, SlotLocationKind>(
    (activityLocations ?? []).map((loc) => [loc.id, loc.kind])
  );
  const ids = s.locationIds ?? [];
  const onlineLocationId = ids.find((id) => locationKindById.get(id) === 'online');
  const physicalLocationId = ids.find((id) => {
    const k = locationKindById.get(id);
    return k === 'venue' || k === 'at_home';
  });

  return {
    id: s.id,
    label: s.label,
    activityId: s.activityId,
    type: s.type,
    maxPeople: s.maxPeople,
    startDate: s.anchorDate,
    closeDate: s.closeDate,
    repeat: s.repeat,
    repeatUntil: s.repeatUntil,
    startTime: s.startTime,
    endTime: s.endTime,
    price: s.price,
    fixedDurations: s.fixedDurations ?? undefined,
    priceByDuration: s.priceByDuration ?? undefined,
    status: 'ACTIVE',
    paymentMethods: paymentMethodsOf(s),
    firstBookingOnline: s.firstBookingOnline ?? false,
    deliveryMode: deliveryModeOf(s.locationIds, locationKindById),
    locationIds: s.locationIds ?? undefined,
    physicalLocationId,
    onlineLocationId,
    timezone: s.timezone ?? undefined
  };
}

/**
 * Create slots. Each time window becomes its own slot (a recurring rule when
 * `repeat<>'NONE'`, otherwise a one-off). Returns the created slots.
 */
export const createSlot = async (request: CreateSlotRequest): Promise<Slot[]> => {
  try {
    const created = await Promise.all(
      request.timeWindows.map((window) => {
        const body: CreateSlotBody = {
          activityId: request.activityId,
          label: request.label,
          type: request.type,
          maxPeople: request.maxPeople,
          anchorDate: window.date ?? request.startDate,
          ...(request.closeDate ? { closeDate: request.closeDate } : {}),
          repeat: request.repeat,
          ...(request.repeatUntil ? { repeatUntil: request.repeatUntil } : {}),
          startTime: window.startTime,
          endTime: window.endTime,
          price: window.price,
          ...(request.fixedDurations !== undefined ? { fixedDurations: request.fixedDurations } : {}),
          ...(request.priceByDuration !== undefined ? { priceByDuration: request.priceByDuration } : {}),
          acceptsCash: request.acceptsCash ?? false,
          acceptsOnline: request.acceptsOnline ?? false,
          firstBookingOnline: request.firstBookingOnline ?? false,
          ...(request.locationIds !== undefined ? { locationIds: request.locationIds } : {})
        };
        return ApiClient.createSlot(body);
      })
    );
    return created.map((slot) => mapApiSlot(slot));
  } catch (error) {
    console.error('Failed to create slot:', error);
    // Keep the API's error so the caller can explain a refusal it knows (its code).
    if (error instanceof ApiError) throw error;
    throw new Error('Failed to create slot. Please try again.');
  }
};

/**
 * Get a specific slot by ID.
 *
 * Pass `activityLocations` (the parent activity's id->kind rows) so `mapApiSlot` can
 * derive `deliveryMode` + the physical/online split correctly. Omitting it (the API
 * slot carries no kinds) makes those derived fields collapse to the in-person default,
 * so a caller that needs the real delivery kind MUST supply the activity's locations.
 */
export const getSlotById = async (
  id: string,
  activityLocations?: ReadonlyArray<{ id: string; kind: SlotLocationKind }>
): Promise<Slot | null> => {
  try {
    const slot = await ApiClient.getSlotById(id, { filterInvalidOnlinePayment: false });
    return slot ? mapApiSlot(slot, activityLocations) : null;
  } catch (error) {
    console.error(`Error fetching slot with ID ${id}:`, error);
    return null;
  }
};

export interface UpdateSlotRequest {
  label?: string;
  type?: SlotType;
  maxPeople?: number;
  anchorDate?: string;
  closeDate?: string;
  repeat?: SlotRepeat;
  repeatUntil?: string;
  startTime?: number;
  endTime?: number;
  price?: number;
  /** RANGE only - the exact bookable durations (minutes). */
  fixedDurations?: number[] | null;
  /** RANGE only - the flat price for each bookable duration (map keyed by minutes, as strings on the wire). */
  priceByDuration?: Record<string, number> | null;
  acceptsCash?: boolean;
  acceptsOnline?: boolean;
  firstBookingOnline?: boolean;
  /**
   * The activity_location ids the slot is offered at. Empty -> the backend
   * defaults to the activity's in-person location. The KIND lives on each
   * activity_location, not on the slot.
   */
  locationIds?: string[];
}

/**
 * Update a slot (the rule, affecting the whole series).
 *
 * `fromDate` turns a whole-series edit into a "this and following" split.
 * `bookedStrategy` controls how future bookings are handled when the edit moves
 * the date (`updates.anchorDate`): 'move' shifts booked occurrences too,
 * 'leave' keeps them on their original dates.
 */
export const updateSlot = async (
  id: string,
  updates: UpdateSlotRequest,
  fromDate?: string,
  bookedStrategy?: 'move' | 'leave'
): Promise<Slot | null> => {
  try {
    const updateBody: UpdateSlotBodyUpdates = {
      label: updates.label,
      type: updates.type,
      maxPeople: updates.maxPeople,
      anchorDate: updates.anchorDate,
      closeDate: updates.closeDate,
      repeat: updates.repeat,
      repeatUntil: updates.repeatUntil,
      startTime: updates.startTime,
      endTime: updates.endTime,
      price: updates.price,
      fixedDurations: updates.fixedDurations,
      priceByDuration: updates.priceByDuration,
      acceptsCash: updates.acceptsCash,
      acceptsOnline: updates.acceptsOnline,
      firstBookingOnline: updates.firstBookingOnline,
      locationIds: updates.locationIds
    };
    const strategy = bookedStrategy as UpdateSlotBodyBookedStrategy | undefined;
    const updated = await ApiClient.updateSlot(
      id,
      fromDate
        ? {
            scope: UpdateSlotBodyScope.thisAndFollowing,
            occurrenceDate: fromDate,
            ...(strategy ? { bookedStrategy: strategy } : {}),
            updates: updateBody
          }
        : { scope: UpdateSlotBodyScope.series, ...(strategy ? { bookedStrategy: strategy } : {}), updates: updateBody }
    );
    return updated ? mapApiSlot(updated) : null;
  } catch (error) {
    // Re-throw so the caller surfaces the API's business-rule reason (e.g.
    // "Cannot change the slot kind while it has bookings") instead of silently
    // swallowing it - a swallowed null read as success and reverted on refetch.
    console.error(`Error updating slot with ID ${id}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Delete a slot via the guarded hard-delete endpoint. The backend removes the
 * slot + its children, but refuses (400) when bookings exist, steering the user
 * to Archive instead. We re-throw so the caller can surface that specific message.
 */
export const deleteSlot = async (id: string): Promise<Slot | null> => {
  try {
    await ApiClient.deleteSlot(id);
    return null;
  } catch (error) {
    console.error(`Error deleting slot with ID ${id}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Archive a slot - hides it and stops new bookings while preserving existing
 * bookings.
 */
export const archiveSlot = async (id: string): Promise<Slot | null> => {
  try {
    const archived = await ApiClient.archiveSlot(id, {});
    return archived ? mapApiSlot(archived) : null;
  } catch (error) {
    console.error(`Error archiving slot with ID ${id}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Restore an archived slot back to active.
 */
export const restoreSlot = async (id: string): Promise<Slot | null> => {
  try {
    const restored = await ApiClient.restoreSlot(id, {});
    return restored ? mapApiSlot(restored) : null;
  } catch (error) {
    console.error(`Error restoring slot with ID ${id}:`, error);
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Copy an existing slot to a new one-off at a given date/time.
 */
export const copySlot = async (params: {
  sourceSlotId: string;
  anchorDate: string;
  startTime?: number;
  endTime?: number;
}): Promise<Slot> => {
  const created = await ApiClient.copySlot(params);
  return mapApiSlot(created);
};

/**
 * Hard delete a slot - permanently removes the slot and all its children.
 */
export const hardDeleteSlot = async (
  id: string
): Promise<{ success: boolean; deletedSlotId: string; deletedChildCount: number }> => {
  try {
    const result = await ApiClient.deleteSlot(id);
    return result;
  } catch (error) {
    console.error(`Error hard deleting slot with ID ${id}:`, error);
    throw new Error('Failed to delete slot. Please try again.');
  }
};
